import { useState, useEffect } from 'react';
import { AnimFloatIdle } from '@readdy/anim/card/float-idle/react';

export default function HeroSection() {
  const trustedLogos = [
    'RetailPro', 'Greenspace Design', 'Mantra School of Dance', 'BuildRight', 'AutoHub',
    'ShopEase', 'MA Traders', 'TravelMate', 'FoodChain', 'HealthPlus',
  ];

  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <section id="hero" className="relative min-h-[100svh] flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://storage.readdy-site.link/project_files/97de57b0-ad1c-46f5-89d0-1b80c93be7d3/2075b43f-33ee-4517-a600-907b682a1419_compressed_ChatGPT-Image-Jul-18-2026-06_00_34-PM.webp"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary-950/70 via-secondary-950/50 to-secondary-950/80"></div>
      </div>

      {/* Floating decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-[10%] w-64 h-64 rounded-full bg-primary-500/10 blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-[5%] w-96 h-96 rounded-full bg-accent-500/8 blur-3xl" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary-500/5 blur-3xl"></div>
      </div>

      <AnimFloatIdle duration={3000} disabled={reducedMotion}>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-28">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left Content */}
          <div className="flex-1 text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 mb-8">
              <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse"></span>
              <span className="text-sm font-medium text-white/90">#1 WhatsApp Automation Platform</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-heading text-white leading-[1.08] mb-6">
              Turn Every{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 via-primary-300 to-accent-400">
                WhatsApp
              </span>
              {' '}Conversation into Business Growth
            </h1>

            <p className="text-lg md:text-xl text-white/70 max-w-xl mx-auto lg:mx-0 mb-4 leading-relaxed">
              From the first customer message to the final sale — automate your entire business communication with AI-powered WhatsApp automation. No coding required.
            </p>

            <p className="text-sm md:text-base text-white/50 max-w-lg mx-auto lg:mx-0 mb-10 leading-relaxed">
              Join 100+ businesses that have automated 1000+ conversations and generated Rs10,00,000+ in revenue through DGFlow.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center gap-3 justify-center lg:justify-start">
              <a
                href="#cta"
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-semibold bg-primary-500 text-white hover:bg-primary-400 transition-all duration-300 hover:scale-[1.02] whitespace-nowrap shadow-lg shadow-primary-500/25"
              >
                Book a Free Demo
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </a>
              <a
                href="https://wa.me/919746765488?text=Hi,%0A%0AI%20want%20to%20know%20more%20about%20your%20services."
                target="_blank"
                rel="noopener noreferrer"
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-semibold border-2 border-white/30 text-white hover:bg-white/10 transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-xl text-primary-400"></i>
                Chat on WhatsApp
              </a>
            </div>

            {/* Trusted By */}
            <div className="mt-14 pt-10 border-t border-white/10">
              <p className="text-xs font-medium text-white/40 uppercase tracking-widest mb-5 text-center lg:text-left">
                Trusted by 100+ businesses worldwide
              </p>
              <div className="flex flex-wrap items-center gap-6 justify-center lg:justify-start">
                {trustedLogos.map(name => (
                  <span key={name} className="text-sm font-semibold text-white/25 tracking-wide">
                    {name}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Real Dashboard Preview */}
          <div className="flex-1 w-full max-w-xl lg:max-w-none">
            <div className="relative">
              {/* Glow behind mockup */}
              <div className="absolute -inset-4 bg-gradient-to-tr from-primary-500/25 via-accent-500/10 to-transparent rounded-3xl blur-2xl"></div>

              {/* Browser-style frame */}
              <div className="relative bg-secondary-900/85 backdrop-blur-sm border border-white/12 rounded-2xl overflow-hidden shadow-2xl">
                {/* Frame header bar */}
                <div className="flex items-center gap-2 px-5 py-3 border-b border-white/8 bg-secondary-950/40">
                  <div className="flex gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-red-400/60"></span>
                    <span className="w-3 h-3 rounded-full bg-yellow-400/60"></span>
                    <span className="w-3 h-3 rounded-full bg-green-400/60"></span>
                  </div>
                  <div className="flex-1 flex justify-center">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 text-[11px] text-white/40">
                      <i className="ri-lock-line text-[10px]"></i>
                      https://automate.dgflow.in/
                    </span>
                  </div>
                </div>

                {/* Real dashboard screenshot — natural proportions, no crop, no distortion */}
                <img
                  src="https://static.readdy.ai/image/537c5a052bbf78982938b46afa25335a/d7d626004db30d1b6a9b4989682efd68.png"
                  alt="DGFlow WhatsApp automation dashboard showing message analytics, response by platform and CRM overview"
                  title="DGFlow Dashboard - WhatsApp Business Automation"
                  className="block w-full h-auto"
                />
              </div>

              {/* Floating live badge */}
              <div className="absolute -bottom-4 -left-4 hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white shadow-xl">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/15">
                  <i className="ri-line-chart-line text-primary-600"></i>
                </span>
                <div>
                  <p className="text-[11px] font-bold text-secondary-950 leading-tight">Live Analytics</p>
                  <p className="text-[10px] text-secondary-500 leading-tight">Real-time insights</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </AnimFloatIdle>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background-50 to-transparent pointer-events-none"></div>
    </section>
  );
}